import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROPERTY_TYPES } from "@/lib/propertyTypes";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";

const inputCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";

function TypeToggle({ form, setForm }) {
  const { lang } = useApp();
  const opts = [
    { key: "sale", label: t("mandate_option_sale", lang), role: "Owner" },
    { key: "search", label: t("mandate_option_search", lang), role: "Buyer" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          data-testid={`mandate-type-${o.key}`}
          onClick={() => setForm({ ...form, mandate_type: o.key })}
          className={`rounded-lg border p-3 text-sm text-start transition-colors duration-200 ${
            form.mandate_type === o.key
              ? "border-[#C5A880] bg-[#C5A880]/10 text-[#1A1610]"
              : "border-[#D8CFBB] text-[#6B5F4C] hover:bg-[#EBE3D3]"
          }`}
        >
          <p className="text-xs tracking-widest uppercase text-[#C5A880] mb-1">{o.role}</p>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function IdentityFields({ form, setForm }) {
  const { lang } = useApp();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label className="text-[#4A3F30]">{t("full_name_label", lang)}</Label>
        <Input data-testid="mandate-name-input" className={inputCls} value={form.signer_name} onChange={set("signer_name")} />
      </div>
      <div>
        <Label className="text-[#4A3F30]">Email</Label>
        <Input data-testid="mandate-email-input" className={inputCls} type="email" value={form.signer_email} onChange={set("signer_email")} />
      </div>
      <div>
        <Label className="text-[#4A3F30]">{t("fiscal_code_label", lang)}</Label>
        <Input className={inputCls} value={form.signer_fiscal_code} onChange={set("signer_fiscal_code")} />
      </div>
      <div>
        <Label className="text-[#4A3F30]">{t("address_label", lang)}</Label>
        <Input className={inputCls} value={form.signer_address} onChange={set("signer_address")} />
      </div>
    </div>
  );
}

function PropertyTypeField({ form, setForm }) {
  const { lang } = useApp();
  return (
    <div>
      <Label className="text-[#4A3F30]">
        {form.mandate_type === "sale" ? "Tipo di immobile" : "Tipo di immobile cercato"}
      </Label>
      <Select value={form.property_type || ""} onValueChange={(v) => setForm({ ...form, property_type: v })}>
        <SelectTrigger data-testid="mandate-type-select" className="bg-[#F5F0E6] border-[#D8CFBB]">
          <SelectValue placeholder="Seleziona il tipo…" />
        </SelectTrigger>
        <SelectContent>
          {PROPERTY_TYPES.map((pt) => (
            <SelectItem key={pt.value} value={pt.value} data-testid={`mandate-type-opt-${pt.value}`}>
              {pt[lang] || pt.it}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SubjectFields({ form, setForm }) {
  const { lang } = useApp();
  if (form.mandate_type === "sale") {
    return (
      <div>
        <Label className="text-[#4A3F30]">{t("property_ref_label", lang)}</Label>
        <Textarea
          data-testid="mandate-property-input"
          className={inputCls}
          rows={2}
          value={form.property_ref}
          onChange={(e) => setForm({ ...form, property_ref: e.target.value })}
        />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-[#4A3F30]">{t("search_area_label", lang)}</Label>
        <Input
          data-testid="mandate-area-input"
          className={inputCls}
          value={form.search_area}
          onChange={(e) => setForm({ ...form, search_area: e.target.value })}
          placeholder="Roma centro storico"
        />
      </div>
      <div>
        <Label className="text-[#4A3F30]">{t("budget_label", lang)}</Label>
        <Input
          data-testid="mandate-budget-input"
          className={inputCls}
          type="number"
          value={form.search_budget}
          onChange={(e) => setForm({ ...form, search_budget: e.target.value })}
          placeholder="2000000"
        />
      </div>
    </div>
  );
}

function TermsFields({ form, setForm }) {
  const { lang } = useApp();
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-[#4A3F30]">{t("duration_months_label", lang)}</Label>
        <Input
          className={inputCls}
          type="number"
          value={form.exclusivity_months}
          onChange={(e) => setForm({ ...form, exclusivity_months: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-[#4A3F30]">{t("commission_terms_label", lang)}</Label>
        <Input
          className={inputCls}
          value={form.commission_terms}
          onChange={(e) => setForm({ ...form, commission_terms: e.target.value })}
          placeholder={t("commission_placeholder", lang)}
        />
      </div>
    </div>
  );
}

function OtpMandateBox({ accepted, form, otpRequested, otpCode, setOtpCode, otpEmail, onRequest }) {
  const { lang } = useApp();
  return (
    <div className="rounded-md border border-[#D8CFBB] bg-[#FFFAF0] p-3">
      <p className="text-[10px] tracking-widest uppercase text-[#C5A880] mb-2">{t("sign_otp_optional", lang)}</p>
      {!otpRequested && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRequest}
          disabled={!accepted || !form.signer_email}
          data-testid="mandate-otp-request"
          className="border-[#C5A880]/50 text-[#4A3F30] bg-transparent hover:bg-[#EBE3D3] rounded-full"
        >
          {t("get_code_email", lang)}
        </Button>
      )}
      {otpRequested && (
        <div className="flex gap-2 items-center">
          <Input
            className={`${inputCls} font-mono tracking-[0.4em] text-center`}
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="mandate-otp-input"
            placeholder="000000"
          />
          <span className="text-xs text-[#7A6E5A]">{otpEmail}</span>
        </div>
      )}
    </div>
  );
}

export default function MandateForm({
  form,
  setForm,
  accepted,
  setAccepted,
  setSignature,
  otpRequested,
  otpCode,
  setOtpCode,
  otpEmail,
  onRequestOtp,
  onPreview,
}) {
  const { lang } = useApp();
  return (
    <div className="grid gap-4 py-3">
      <TypeToggle form={form} setForm={setForm} />
      <IdentityFields form={form} setForm={setForm} />
      <PropertyTypeField form={form} setForm={setForm} />
      <SubjectFields form={form} setForm={setForm} />
      <TermsFields form={form} setForm={setForm} />
      <div className="rounded-md border border-[#D8CFBB] bg-[#F5F0E6] p-3 text-xs text-[#6B5F4C] leading-relaxed">
        {t("mandate_terms_notice", lang)}
      </div>
      <SignaturePad onChange={setSignature} testId="mandate-signature-pad" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Checkbox id="mandate-accept" checked={accepted} onCheckedChange={setAccepted} data-testid="mandate-accept-checkbox" />
          <Label htmlFor="mandate-accept" className="text-[#2E2A22] cursor-pointer text-sm">
            {t("mandate_accept_label", lang)}
          </Label>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onPreview}
          disabled={!form.signer_name}
          data-testid="mandate-preview-btn"
          className="border-[#C5A880] text-[#4A3F30] bg-transparent hover:bg-[#EBE3D3] rounded-full"
        >
          <Eye className="h-3.5 w-3.5 me-1.5" />
          {t("preview_draft", lang)}
        </Button>
      </div>
      <OtpMandateBox
        accepted={accepted}
        form={form}
        otpRequested={otpRequested}
        otpCode={otpCode}
        setOtpCode={setOtpCode}
        otpEmail={otpEmail}
        onRequest={onRequestOtp}
      />
    </div>
  );
}
